/**
 * Date preset utilities for flexible date range selection
 * All dates are calculated in local timezone
 */

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

/**
 * Get date range for last N days (inclusive of today)
 */
export const getLastNDays = (days: number): DateRange => {
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999); // End of today

  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days + 1); // Include today in count
  startDate.setHours(0, 0, 0, 0); // Start of day

  return { startDate, endDate };
};

/**
 * Get date range for current month
 */
export const getThisMonth = (): DateRange => {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  endDate.setHours(23, 59, 59, 999);

  return { startDate, endDate };
};

/**
 * Get date range for last month
 */
export const getLastMonth = (): DateRange => {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(now.getFullYear(), now.getMonth(), 0);
  endDate.setHours(23, 59, 59, 999);

  return { startDate, endDate };
};

/**
 * Get date range for last N months (including current month)
 */
export const getLastNMonths = (months: number): DateRange => {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth() - months + 1, 1);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);

  return { startDate, endDate };
};

/**
 * Get date range for last year (365 days)
 */
export const getLastYear = (): DateRange => {
  return getLastNDays(365);
};

/**
 * Get "all time" date range (starts from a reasonable minimum date)
 * Using January 1, 2020 as the earliest reasonable date
 */
export const getAllTime = (): DateRange => {
  const startDate = new Date(2020, 0, 1);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);

  return { startDate, endDate };
};

/**
 * Format date for API (YYYY-MM-DD)
 */
export const formatDateForAPI = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get default date range (1 month ago to today)
 */
export const getDefaultDateRange = (): DateRange => {
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);

  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 1);
  startDate.setHours(0, 0, 0, 0);

  return { startDate, endDate };
};

/**
 * Preset options for dropdown
 */
export const PRESET_OPTIONS = [
  { label: 'Last 30 days', value: 'last_30_days', getDateRange: () => getLastNDays(30) },
  { label: 'This month', value: 'this_month', getDateRange: getThisMonth },
  { label: 'Last month', value: 'last_month', getDateRange: getLastMonth },
  { label: 'Last 3 months', value: 'last_3_months', getDateRange: () => getLastNMonths(3) },
  { label: 'Last 6 months', value: 'last_6_months', getDateRange: () => getLastNMonths(6) },
  { label: 'Last year', value: 'last_year', getDateRange: getLastYear },
  { label: 'All time', value: 'all_time', getDateRange: getAllTime },
];